import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { Callout } from "@/components/ui";

/**
 * Router context.
 *
 * The QueryClient rides in the context so `beforeLoad` guards can await the
 * session through the same cache the components read — one request, and the
 * component never re-fetches what the guard already resolved.
 */
export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: NotFound,
  errorComponent: ErrorScreen,
});

function NotFound() {
  return (
    <main className="shell grid min-h-dvh place-items-center py-20">
      <div className="flex max-w-[46ch] flex-col gap-4 text-center">
        <p className="mono text-ink-3">404</p>
        <h1 className="display text-[clamp(28px,5vw,42px)]">
          Nothing here.
        </h1>
        <p className="text-ink-2">
          That page does not exist, or it did and you are not signed in for it.
        </p>
        <a className="mono underline underline-offset-4" href="/portal/">
          Back to the portal
        </a>
      </div>
    </main>
  );
}

function ErrorScreen({ error }: { error: Error }) {
  return (
    <main className="shell grid min-h-dvh place-items-center py-20">
      <div className="flex max-w-[52ch] flex-col gap-5">
        <p className="mono text-ink-3">Something broke</p>
        <h1 className="display text-[clamp(26px,4vw,36px)]">
          The portal could not load that.
        </h1>
        <Callout tone="error">{error.message}</Callout>
        <p className="text-[14px] text-ink-2">
          If it keeps happening, tell your vertical&rsquo;s coordinator what you
          were doing — that sentence above is the useful part.
        </p>
      </div>
    </main>
  );
}
